import {createContext, useContext} from "react"
import {PublicKey} from "@solana/web3.js"
import bs58 from "bs58"
import {useProgram} from "@/context/ProgramContext.tsx"
import {IdlAccounts} from "@coral-xyz/anchor"
import {Revshare} from "@/components/SolanaPools/anchor-idl/idl.ts"
import * as anchor from "@coral-xyz/anchor"

export type GetAllUserPools = (userPub58: string) => Promise<{
  publicKey: PublicKey,
  account: IdlAccounts<Revshare>["userStateV2"]
}[]>

export type GetAllPoolsState = () => Promise<{
  publicKey: PublicKey,
  account: IdlAccounts<Revshare>["zenexPoolStateV2"]
}[]>
export type GetDepositStat = (year: number, month: number, userPub58: string) => Promise<IdlAccounts<Revshare>["userStateV2"]>
export type GetPoolState = (year: number, month: number) => Promise<IdlAccounts<Revshare>["zenexPoolStateV2"]>

type UserPoolsContextType = {
  getAllUserPools: GetAllUserPools
  getAllPoolsState: GetAllPoolsState
  getDepositStat: GetDepositStat
  getPoolState: GetPoolState
  getAllPoolUsers:(year: number, month: number) => Promise<{publicKey: PublicKey,account:IdlAccounts<Revshare>["userStateV2"] }[]>
}

const UserPoolsContext = createContext<UserPoolsContextType | undefined>(undefined)

export const UserPoolsProvider = ({children}: { children: React.ReactNode }) => {
  const {program} = useProgram()



  // вернет статистику участия всех пулов в которых участвует пользователь
  const getAllUserPools = async (userPub58: string) => {
    if (!program) throw new Error("Program is not initialized")
    return await program.account.userStateV2.all(
      [
      {
        memcmp: {
          offset: 8,
          bytes: userPub58,
        }
      },
    ]
    )
  }

  const getAllPoolsState = async () => {
    if (!program) throw new Error("Program is not initialized")
    return await program.account.zenexPoolStateV2.all()
  }

  // Получение статистики участия в конкретном пуле по году и месяцу
  const getDepositStat = async (year: number, month: number, userPub58: string) => {
    if (!program) throw new Error("Program is not initialized")
    const pda = PublicKey.findProgramAddressSync(
      [
        Buffer.from("user"),
        bs58.decode(userPub58),
        Buffer.from([2]),
        Buffer.from(Uint16Array.of(year).buffer),
        Buffer.from([month]),
      ],
      program.programId
    )[0]
    return program.account.userStateV2.fetch(pda)
  }

  // функция для получения PDA пула по году и месяцу
  const poolPda = (year: number, month: number, programId: PublicKey): PublicKey => {
    if (!program) throw new Error("Program is not initialized")

    return anchor.web3.PublicKey.findProgramAddressSync(
      [
        Buffer.from("pools"),
        Buffer.from([2]),
        Buffer.from(Uint16Array.of(year).buffer), // u16 little-endian
        Buffer.from([month]), // u8
      ],
      programId
    )[0]
  }

  // возвращается информацию по конкретному пулу за год и месяц
  const getPoolState = async (year: number, month: number) => {
    if (!program) throw new Error("Program is not initialized")

    const pda = poolPda(year, month, program.programId)
    return await program.account.zenexPoolStateV2.fetch(pda)
  }

  function getAllPoolUsers (year: number, month: number) {
    if (!program) throw new Error("Program is not initialized")

    const data = program.account.userStateV2.all([

      {
        memcmp: {
          offset: 40,
          bytes: bs58.encode(Buffer.from([2])),
        }
      },
      {
        memcmp: {
          offset: 41,
          bytes: bs58.encode(Buffer.from(Uint16Array.of(year).buffer)),
        }
      },
      {
        memcmp: {
          offset: 43,
          bytes: bs58.encode(Buffer.from([month])),
        }
      }

    ]);
    return data as  Promise<{publicKey: PublicKey,account:IdlAccounts<Revshare>["userStateV2"] }[]>;
  }

  return (
    <UserPoolsContext.Provider value={{getAllUserPools, getAllPoolsState, getDepositStat, getPoolState, getAllPoolUsers}}>
      {children}
    </UserPoolsContext.Provider>
  )
}

export const useUserPools = () => {
  const ctx = useContext(UserPoolsContext)
  if (!ctx) throw new Error("useUserPools must be used within a UserPoolsProvider")
  return ctx
}
